import { Link } from "react-router-dom";
import { resolveWorkMoment, roleDefinitions, rolePath } from "../catalogue";
import { useCopy, useFramework } from "../framework";
import { humanize } from "./ui";

/**
 * The Work moment drawn as a visual decision trace: signal, evidence, agent
 * proposal, human decision, committed outcome. Every word renders from the
 * governed moment, fixture and agent records — the component adds stage
 * kickers and composition only — and the illustrative and synthetic marks
 * never leave the frame. The compact variant illustrates a domain page's
 * flagship scene; the full variant opens the moment detail.
 */

const stateFamily: Record<string, string> = {
  normal: "neutral",
  exception: "danger",
  stale: "danger",
  conflicting: "warning",
  proposal: "info",
  proposed: "info",
  "awaiting-review": "info",
  "in-review": "info",
  committing: "info",
  reviewed: "success",
  committed: "success"
};

function StateWord({ state, label }: { state: string; label: string }) {
  return (
    <span className={`apf-moment-illustration__state apf-moment-illustration__state--${stateFamily[state] ?? "neutral"}`}>
      {label}
    </span>
  );
}

export function WorkMomentIllustration({
  momentId,
  roleId,
  variant = "compact"
}: {
  momentId: string;
  roleId?: string;
  variant?: "compact" | "full";
}) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const resolved = resolveWorkMoment(snapshot, momentId, roleId);
  if (!resolved) return null;
  const { definition, perspective, agent, fixture } = resolved;
  const roles = roleDefinitions(snapshot);
  const roleLabel = (id: string) => {
    const role = roles.find(({ definition: candidate }) => candidate.id === id)?.definition;
    return role ? copy(role.labelKey) : humanize(id);
  };
  const stateLabel = (state: string) => copy(`work-moment.state.${state}`, humanize(state));
  const superviseMode = fixture.modes["supervise-work"];
  const allRecords = Object.values(fixture.modes).flatMap((mode) => mode?.records ?? []);
  const signalRecord = allRecords.find(({ state }) => state === "exception") ?? superviseMode?.records[0];
  const proposalRecord = allRecords.find(({ state }) => state === "proposal");
  const committedRecord =
    allRecords.find(({ state }) => state === "committed") ??
    fixture.modes["open-office-artifact"]?.records[0];
  const full = variant === "full";
  return (
    <figure className={`apf-moment-illustration apf-moment-illustration--${variant}`}>
      <figcaption className="apf-moment-illustration__caption">
        <span className="apf-moment-illustration__title">
          {copy("work-moment.illustration.label", "How the moment unfolds")}
        </span>
        <span className="apf-proof-line">
          <span>{copy("work-moment.proof.illustrative", "Illustrative work moment")}</span>
          <span>{copy("work-moment.proof.synthetic", "Synthetic data")}</span>
        </span>
      </figcaption>
      <ol className="apf-moment-illustration__trace">
        <li className="apf-moment-illustration__step">
          <span className="apf-moment-illustration__kicker">{copy("work-moment.illustration.signal", "The signal")}</span>
          {signalRecord ? (
            <>
              <strong className="apf-moment-illustration__label">{copy(signalRecord.labelKey)}</strong>
              {full ? <span className="apf-moment-illustration__detail">{copy(signalRecord.detailKey)}</span> : null}
              <span className="apf-moment-illustration__meta">
                <StateWord state={signalRecord.state} label={stateLabel(signalRecord.state)} />
                <span className="apf-moment-illustration__owner">{roleLabel(signalRecord.ownerRoleId)}</span>
              </span>
            </>
          ) : (
            <strong className="apf-moment-illustration__label">{copy(definition.summaryKey)}</strong>
          )}
        </li>
        <li className="apf-moment-illustration__step">
          <span className="apf-moment-illustration__kicker">{copy("work-moment.illustration.evidence", "The evidence")}</span>
          <ul className="apf-moment-illustration__evidence">
            {fixture.evidenceRecords.map((record) => (
              <li key={record.id}>
                <strong>{copy(record.labelKey)}</strong>
                <span className={`apf-moment-illustration__freshness apf-moment-illustration__freshness--${record.freshness}`}>
                  {copy(`work-moment.freshness.${record.freshness}`, humanize(record.freshness))}
                </span>
              </li>
            ))}
          </ul>
        </li>
        <li className="apf-moment-illustration__step">
          <span className="apf-moment-illustration__kicker">{copy("work-moment.illustration.proposal", "The agent proposes")}</span>
          <strong className="apf-moment-illustration__label">
            {proposalRecord ? copy(proposalRecord.labelKey) : agent.label}
          </strong>
          {full ? (
            <span className="apf-moment-illustration__detail">
              {proposalRecord ? copy(proposalRecord.detailKey) : copy(definition.agentContributionKey)}
            </span>
          ) : null}
          <span className="apf-moment-illustration__meta">
            <StateWord state="proposal" label={stateLabel("proposal")} />
            <span className="apf-moment-illustration__owner">{agent.label}</span>
          </span>
        </li>
        <li className="apf-moment-illustration__step">
          <span className="apf-moment-illustration__kicker">{copy("work-moment.illustration.decision", "A person decides")}</span>
          <strong className="apf-moment-illustration__label">
            <Link to={rolePath(perspective.roleId)}>{roleLabel(perspective.roleId)}</Link>
          </strong>
          {full ? <span className="apf-moment-illustration__detail">{copy(perspective.supervisionKey)}</span> : null}
          <span className="apf-moment-illustration__meta">
            <span className="apf-moment-illustration__owner">
              {copy("work-moment.frame.grantor", "Authority grantor")}: {roleLabel(fixture.authorityGrantorRoleId)}
            </span>
          </span>
        </li>
        <li className="apf-moment-illustration__step">
          <span className="apf-moment-illustration__kicker">{copy("work-moment.illustration.committed", "The committed outcome")}</span>
          {committedRecord ? (
            <>
              <strong className="apf-moment-illustration__label">{copy(committedRecord.labelKey)}</strong>
              {full ? <span className="apf-moment-illustration__detail">{copy(committedRecord.detailKey)}</span> : null}
              <span className="apf-moment-illustration__meta">
                <StateWord state={committedRecord.state} label={stateLabel(committedRecord.state)} />
                <span className="apf-moment-illustration__owner">{roleLabel(committedRecord.ownerRoleId)}</span>
              </span>
            </>
          ) : (
            <strong className="apf-moment-illustration__label">{copy(perspective.nowKey)}</strong>
          )}
        </li>
      </ol>
    </figure>
  );
}
